import svgBullet from '@img/icons/bullet-fill.svg?url';
import svgQuote from '@img/icons/quote-testimonials.svg?url';
import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import scrollbars from './scrollbars';
export const headingStyles = css `
  ${fonts.poppins.extraBold};

  color: currentColor;
  margin: 0 0 ${fluid(30, 42)};
  text-wrap: pretty;
`;
export const h1 = css `
  ${headingStyles};

  font-size: ${fluid(38, 72)};
  line-height: ${fluid(46, 88)};
`;
export const h2 = css `
  ${headingStyles};

  font-size: ${fluid(32, 58)};
  line-height: ${fluid(38, 66)};
`;
export const h3 = css `
  ${headingStyles};

  font-size: ${fluid(26, 42)};
  line-height: ${fluid(34, 48)};
`;
export const h4 = css `
  ${headingStyles};

  font-size: ${fluid(22, 32)};
  line-height: ${fluid(30, 38)};
`;
export const h5 = css `
  ${headingStyles};

  font-size: ${fluid(16, 24)};
  line-height: ${fluid(22, 30)};
  font-weight: 700;
`;
export const h6 = css `
  ${headingStyles};

  font-size: ${fluid(16, 18)};
  line-height: 20px;
  font-weight: 700;
`;
export const paragraphStyles = css `
  ${fonts.poppins.regular};

  color: currentColor;
  margin: 0 0 ${fluid(12, 14)};
  text-wrap: pretty;
`;
export const paragraphExtraLarge = css `
  ${paragraphStyles};

  font-size: ${fluid(22, 32)};
  line-height: ${fluid(30, 42)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};

  font-size: ${fluid(17, 22)};
  line-height: ${fluid(25, 36)};
`;
export const paragraphRegular = css `
  ${paragraphStyles};

  font-size: ${fluid(17, 20)};
  line-height: ${fluid(25, 36)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(14, 18)};
  line-height: ${fluid(22, 28)};
`;
export const tag = css `
  ${fonts.poppins.bold};

  font-size: ${fluid(11, 14)};
  line-height: ${fluid(12, 16)};
  margin: 0 0 ${fluid(12, 20)};
`;
export const authorName = css `
  ${fonts.caveat};

  font-size: ${fluid(20, 28)};
  line-height: ${fluid(26, 38)};
`;
export const link = css `
  ${transition('text-decoration-color')};

  --textDecorationColour: ${brand.primary.orange};

  display: inline;
  color: currentColor;
  text-decoration: underline;
  text-decoration-color: var(--textDecorationColour);
  text-decoration-thickness: 1px;
  text-underline-offset: ${fluid(3, 4)};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 2px;
    outline-width: 1px;
    outline-style: solid;
    outline-color: ${brand.primary.orange};
  }

  &:focus,
  &:hover {
    text-decoration-color: transparent;
  }
`;
export const blockquote = css `
  ${paragraphRegular};

  --quoteWidth: ${fluid(56, 106)};
  --quoteColour: linear-gradient(
    180deg,
    ${brand.primary.orange} 63.63%,
    ${brand.secondary.orangeDark} 100%
  );
  --quoteGap: ${fluid(8, 24)};
  --lineWidth: ${fluid(4, 8)};

  position: relative;
  color: currentColor;
  text-align: left;
  font-style: italic;
  padding-left: calc((var(--quoteGap) * 2) + var(--quoteWidth) + var(--lineWidth));

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: var(--quoteColour);
  }

  &::before {
    top: 50%;
    left: 0;
    width: var(--quoteWidth);
    aspect-ratio: 106 / 82;
    -webkit-mask-image: url(${svgQuote});
    mask-image: url(${svgQuote});
    mask-repeat: no-repeat;
    background-size: contain;
    transform: translateY(-50%);
  }

  &::after {
    left: calc(var(--quoteGap) + var(--quoteWidth));
    top: 0;
    width: var(--lineWidth);
    height: 100%;
    border-radius: 100px;
  }

  p {
    color: currentColor;
    font-size: inherit;
    line-height: inherit;
    font-style: inherit;
    margin-bottom: ${fluid(8, 16)};
  }

  footer {
    ${authorName};

    cite {
      ${paragraphRegular};

      margin: 0;
    }
  }
`;
export const table = css `
  ${paragraphSmall};

  --borderRadius: 6px;

  background: ${brand.primary.white};
  border-spacing: 0;
  border-collapse: separate;
  color: currentColor;
  width: 100% !important;
  border-radius: var(--borderRadius);

  td,
  th {
    border: 1px solid ${brand.primary.black};
    padding: 10px 12px;
    text-align: left;

    &:not(:last-child) {
      border-right: 0;
    }
  }

  thead {
    tr {
      background: transparent;

      td,
      th {
        font-weight: 700;
        color: ${brand.primary.orange};
        border-bottom: 0;

        &:first-child {
          border-top-left-radius: var(--borderRadius);
        }

        &:last-child {
          border-top-right-radius: var(--borderRadius);
        }
      }
    }
  }

  tbody {
    tr {
      &:last-child {
        td:first-child {
          border-bottom-left-radius: var(--borderRadius);
        }

        td:last-child {
          border-bottom-right-radius: var(--borderRadius);
        }
      }

      &:not(:last-child) {
        td {
          border-bottom: 0;
        }
      }
    }
  }
`;
export const contentStyles = css `
  --bulletColour: ${brand.primary.orange};

  h1,
  .h1-styles {
    ${h2};
  }

  h2,
  .h2-styles {
    ${h3};
  }

  h3,
  .h3-styles {
    ${h4};
  }

  h4,
  .h4-styles {
    ${h5}
  }

  h5,
  .h5-styles {
    ${h6}
  }

  h6,
  .h6-styles {
    ${h6}
  }

  h1,
  .h1-styles,
  h2,
  .h2-styles,
  h3,
  .h3-styles,
  h4,
  .h4-styles,
  h5,
  .h5-styles,
  h6,
  .h6-styles {
    margin: ${fluid(18, 32)} 0 0.48em;
  }

  p {
    ${paragraphRegular};

    a {
      ${link};
    }
  }

  strong {
    font-weight: 700;
  }

  .extra-large {
    ${paragraphExtraLarge};

    strong {
      font-weight: 800;
    }
  }

  .large {
    ${paragraphLarge}
  }

  .small {
    ${paragraphSmall}
  }

  .tag {
    ${tag}
  }

  a {
    ${link}
  }

  ol,
  ul {
    ${paragraphRegular};

    margin-top: ${fluid(12, 14)};

    li {
      color: currentColor;
      margin: 0 0 ${fluid(10, 12)};
    }
  }

  ol,
  ul {
    list-style: none;
    padding-left: 26px;
  }

  ol {
    counter-reset: li;

    li {
      display: block;
      counter-increment: li;
      margin-left: -26px;

      &::before {
        color: ${brand.primary.orange};
        content: counter(li) '.';
        margin-right: 12px;
      }
    }
  }

  // Ensure that inline styling hasn't been applied
  ul:not([style*='list-style-type']) li {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: ${fluid(5.5, 12)};
      left: -26px;
      height: 13px;
      width: 12px;
      -webkit-mask-image: url(${svgBullet});
      mask-image: url(${svgBullet});
      mask-repeat: no-repeat;
      background-color: var(--bulletColour);
    }
  }

  blockquote {
    ${blockquote};
  }

  hr {
    display: block;
    background: var(--titleHighlight);
    border: 0;
    height: 2px;
    margin: ${fluid(30, 42)} 0;
    padding: 0;
    width: 100%;
    border-radius: 100px;
  }

  table {
    ${table};
  }

  .responsive-table {
    ${scrollbars};

    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0 !important;
  }
`;
